import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import LoadingSpinner from '../components/LoadingSpinner';
import { Building2, MapPin, Users, ArrowRight, CheckCircle } from 'lucide-react';
import useAuthStore from '../stores/authStore';

const TenantOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Step 1: Brand Information
  const [brandData, setBrandData] = useState({
    name: '',
    description: '',
    website: '',
    contact_email: ''
  });

  // Step 2: Hotel Information
  const [hotelData, setHotelData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    star_rating: '3'
  });

  const steps = [
    { number: 1, title: 'Brand Setup', description: 'Create your hotel brand' },
    { number: 2, title: 'Hotel Setup', description: 'Add your first hotel' },
    { number: 3, title: 'Complete', description: 'Ready to use dashboard' }
  ];

  const handleBrandSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/tenant/brands', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('userToken')}`
        },
        body: JSON.stringify({ name: brandData.name })
      });

      if (response.ok) {
        const result = await response.json();
        setBrandData({ ...brandData, id: result.brand.id });
        setCurrentStep(2);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create brand');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHotelSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/tenant/brands/${brandData.id}/hotels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('userToken')}`
        },
        body: JSON.stringify({
          name: hotelData.name,
          city: hotelData.city,
          country: hotelData.country,
          address: hotelData.address,
          phone: hotelData.phone,
          email: hotelData.email,
          star_rating: parseInt(hotelData.star_rating)
        })
      });

      if (response.ok) {
        setCurrentStep(3);
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/app/dashboard');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create hotel');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to Think Robin! 🎉
          </h1>
          <p className="text-gray-600">
            Let's set up your hotel brand and property to get started
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate('/app/dashboard')}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Skip setup - I'll configure this later
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep >= step.number 
                    ? 'bg-primary text-white border-primary' 
                    : 'bg-white text-gray-400 border-gray-300'
                }`}>
                  {currentStep > step.number ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    step.number
                  )}
                </div>
                <div className="ml-2 text-sm">
                  <div className={`font-medium ${currentStep >= step.number ? 'text-primary' : 'text-gray-400'}`}>
                    {step.title}
                  </div>
                  <div className="text-gray-500 text-xs">{step.description}</div>
                </div>
                {index < steps.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-gray-300 mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Brand Setup */}
        {currentStep === 1 && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <CardTitle className="text-2xl">Create Your Hotel Brand</CardTitle>
              <CardDescription>
                Set up your hotel brand information to get started
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBrandSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="brand-name">Brand Name *</Label>
                  <Input
                    id="brand-name"
                    placeholder="e.g., Luxury Hotels Group"
                    value={brandData.name}
                    onChange={(e) => setBrandData({ ...brandData, name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="brand-description">Description</Label>
                  <Input
                    id="brand-description"
                    placeholder="Brief description of your hotel brand"
                    value={brandData.description}
                    onChange={(e) => setBrandData({ ...brandData, description: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="brand-website">Website</Label>
                    <Input
                      id="brand-website"
                      type="url"
                      placeholder="https://yourhotel.com"
                      value={brandData.website}
                      onChange={(e) => setBrandData({ ...brandData, website: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="brand-email">Contact Email</Label>
                    <Input
                      id="brand-email"
                      type="email"
                      placeholder="contact@yourhotel.com"
                      value={brandData.contact_email}
                      onChange={(e) => setBrandData({ ...brandData, contact_email: e.target.value })}
                    />
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={isLoading || !brandData.name}>
                  {isLoading ? <LoadingSpinner size="small" text="" /> : 'Create Brand & Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Hotel Setup */}
        {currentStep === 2 && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <MapPin className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-2xl">Add Your First Hotel</CardTitle>
              <CardDescription>
                Add your hotel property to start analyzing market demand
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleHotelSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="hotel-name">Hotel Name *</Label>
                  <Input
                    id="hotel-name"
                    placeholder="e.g., Grand Hotel Bali"
                    value={hotelData.name}
                    onChange={(e) => setHotelData({ ...hotelData, name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="hotel-address">Address *</Label>
                  <Input
                    id="hotel-address"
                    placeholder="Full hotel address"
                    value={hotelData.address}
                    onChange={(e) => setHotelData({ ...hotelData, address: e.target.value })}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hotel-city">City *</Label>
                    <Input
                      id="hotel-city"
                      placeholder="e.g., Ubud"
                      value={hotelData.city}
                      onChange={(e) => setHotelData({ ...hotelData, city: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="hotel-country">Country *</Label>
                    <Input
                      id="hotel-country"
                      placeholder="e.g., Indonesia"
                      value={hotelData.country}
                      onChange={(e) => setHotelData({ ...hotelData, country: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hotel-phone">Phone</Label>
                    <Input
                      id="hotel-phone"
                      placeholder="+62 361 123456"
                      value={hotelData.phone}
                      onChange={(e) => setHotelData({ ...hotelData, phone: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="hotel-email">Email</Label>
                    <Input
                      id="hotel-email"
                      type="email"
                      placeholder="reservations@hotel.com"
                      value={hotelData.email}
                      onChange={(e) => setHotelData({ ...hotelData, email: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="star-rating">Star Rating</Label>
                  <select
                    id="star-rating"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={hotelData.star_rating}
                    onChange={(e) => setHotelData({ ...hotelData, star_rating: e.target.value })}
                  >
                    <option value="1">1 Star</option>
                    <option value="2">2 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="5">5 Stars</option>
                  </select>
                </div>
                
                <Button type="submit" className="w-full" disabled={isLoading || !hotelData.name || !hotelData.address || !hotelData.city || !hotelData.country}>
                  {isLoading ? <LoadingSpinner size="small" text="" /> : 'Create Hotel & Complete Setup'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Complete */}
        {currentStep === 3 && (
          <Card className="max-w-2xl mx-auto text-center">
            <CardContent className="pt-6">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-green-100 rounded-full">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Setup Complete! 🎉
              </h2>
              <p className="text-gray-600 mb-6">
                Your hotel brand and property have been successfully created. 
                You'll be redirected to your dashboard shortly.
              </p>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <LoadingSpinner size="small" />
                <span>Redirecting to dashboard...</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TenantOnboarding;
